// src/components/Galeria.js
import React from 'react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Button,
  Typography,
  Grid,
  Dialog,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';

// Imágenes
import foto1 from '../assets/img/productos/foto1.jpg';
import foto2 from '../assets/img/productos/foto2.jpg';
import foto3 from '../assets/img/productos/foto3.jpg';
import foto4 from '../assets/img/productos/foto4.jpg';

// Título centrado
import CenteredTitle from './CenteredTitle';

// ======================================
// Datos
// ======================================
const cardData = [
  {
    id: 1,
    title: 'Persiana de Aluminio Inyectada con Poliuretano',
    description:
      'Medidas: 43, 45, 55, 77, 95 y 125 - Colores: blanco, negro, gris plata, marrón veteado',
    image: foto1,
    whas: 'https://walink.co/40b147',
  },
  {
    id: 2,
    title: 'Persianas de aluminio con Cajón compacto',
    description:
      'Además, ofrecemos servicios de motorización de persianas con control remoto',
    image: foto2,
    whas: 'https://walink.co/9c4fd4',
  },
  {
    id: 3,
    title: 'Persianas de PVC económicas, reforzadas y super reforzadas',
    description: 'Además, contamos con herrajes',
    image: foto3,
    whas: 'https://walink.co/a9f094',
  },
  {
    id: 4,
    title: 'Persiana de aluminio extruido – Máxima resistencia',
    description:
      'Persiana de aluminio extruido de máxima resistencia, con un peso aproximado de 7,9 kg por m².',
    image: foto4,
    whas: 'https://wa.link/tqvp9x',
  },
];

// ======================================
// Estilos PRO
// ======================================
const ModernCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 18,
  background: 'linear-gradient(180deg, #0a0a0a 0%, #111 60%, #141414 100%)',
  color: theme.palette.text.primary,
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.06) inset, 0 0 0 1px rgba(218,165,32,0.18), 0 14px 22px rgba(0,0,0,0.45), 0 40px 70px rgba(0,0,0,0.35)',
  transform: 'translateZ(0)',
  transition:
    'transform .28s cubic-bezier(.2,.8,.2,1), box-shadow .3s ease, filter .3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background:
      'linear-gradient(120deg, rgba(218,165,32,0.16) 0%, rgba(218,165,32,0) 35%, rgba(218,165,32,0) 65%, rgba(218,165,32,0.16) 100%)',
    padding: 1,
    borderRadius: 18,
    mixBlendMode: 'soft-light',
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.01)',
    boxShadow:
      '0 1px 0 rgba(255,255,255,0.06) inset, 0 0 0 1px rgba(218,165,32,0.35), 0 18px 28px rgba(0,0,0,0.55), 0 60px 90px rgba(0,0,0,0.45)',
    filter: 'saturate(1.05)',
  },
}));

// Contenedor de imagen (pro catálogo): cuadrada en mobile, 4:3 en desktop
const MediaWrap = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  aspectRatio: '1 / 1',
  [theme.breakpoints.up('md')]: {
    aspectRatio: '4 / 3',
  },
  borderBottom: '1px solid rgba(218,165,32,.18)',
  background:
    'radial-gradient(80% 60% at 50% 40%, rgba(255,255,255,.08) 0%, rgba(255,255,255,0) 100%), linear-gradient(180deg, #171717 0%, #0c0c0c 100%)',
}));

// Imagen PRO: se ve completa (contain), sin recorte, con marco/padding elegante
const Media = styled(CardMedia)(({ loaded }) => ({
  height: '100%',
  width: '100%',
  objectFit: 'contain', // ✅ se ve completa
  objectPosition: 'center',
  padding: 14, // ✅ “marco” interno pro
  transform: 'scale(1)',
  transition: 'transform .6s cubic-bezier(.2,.8,.2,1), filter .4s ease',
  filter: loaded ? 'none' : 'blur(10px) brightness(.9)',
  background:
    'radial-gradient(80% 60% at 50% 40%, rgba(255,255,255,.08) 0%, rgba(255,255,255,0) 100%), linear-gradient(180deg, #1a1a1a 0%, #0c0c0c 100%)',
  '.MuiCard-root:hover &': { transform: 'scale(1.03)' }, // zoom suave sin recorte
}));

const MediaOverlayTop = styled('div')({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(180deg, rgba(0,0,0,.35) 0%, rgba(0,0,0,.12) 35%, rgba(0,0,0,0) 60%)',
  pointerEvents: 'none',
});

const MediaOverlayBottom = styled('div')({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,.45) 100%)',
  pointerEvents: 'none',
});

const PreviewButton = styled(IconButton)({
  position: 'absolute',
  right: 10,
  top: 10,
  background: 'rgba(20,20,20,.55)',
  border: '1px solid rgba(218,165,32,.35)',
  backdropFilter: 'blur(4px)',
  color: '#F5DEB3',
  '&:hover': {
    background: '#000',
    color: '#DAA520',
  },
});

const SolidButton = styled(Button)({
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  fontWeight: 800,
  borderRadius: 12,
  padding: '10px 18px',
  backgroundColor: '#DAA520',
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.2) inset, 0 10px 20px rgba(218,165,32,0.35)',
  transition: 'transform .2s ease, box-shadow .25s ease, filter .25s ease',
  '&:hover': {
    backgroundColor: '#000',
    color: '#DAA520',
    boxShadow:
      '0 12px 28px rgba(0,0,0,.45), 0 0 0 1px rgba(218,165,32,.4) inset',
    transform: 'translateY(-2px)',
  },
  '&:active': { transform: 'translateY(0)' },
});

// Imagen grande dentro del lightbox: completa (contain) + pan/zoom
const ZoomableImg = styled('img')({
  maxWidth: '100%',
  maxHeight: '78vh',
  display: 'block',
  margin: '0 auto',
  userSelect: 'none',
  cursor: 'grab',
  objectFit: 'contain',
  background: 'transparent',
});

// ======================================
// Componente
// ======================================
const Galeria = () => {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxSrc, setLightboxSrc] = React.useState(null);
  const [lightboxTitle, setLightboxTitle] = React.useState('');
  const [imgLoaded, setImgLoaded] = React.useState({}); // {id: true/false}

  // pan/zoom state
  const imgRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);
  const [pos, setPos] = React.useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = React.useState(false);
  const [start, setStart] = React.useState({ x: 0, y: 0 });

  const openLightbox = (src, title) => {
    setLightboxSrc(src);
    setLightboxTitle(title);
    setScale(1);
    setPos({ x: 0, y: 0 });
    setLightboxOpen(true);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale((s) => Math.min(3, Math.max(1, s + delta)));
  };

  const onMouseDown = (e) => {
    setIsPanning(true);
    setStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };
  const onMouseMove = (e) => {
    if (!isPanning) return;
    setPos({ x: e.clientX - start.x, y: e.clientY - start.y });
  };
  const onMouseUp = () => setIsPanning(false);

  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsPanning(true);
      setStart({
        x: e.touches[0].clientX - pos.x,
        y: e.touches[0].clientY - pos.y,
      });
    }
  };
  const onTouchMove = (e) => {
    if (!isPanning || e.touches.length !== 1) return;
    setPos({
      x: e.touches[0].clientX - start.x,
      y: e.touches[0].clientY - start.y,
    });
  };
  const onTouchEnd = () => setIsPanning(false);

  return (
    <Box
      sx={{
        p: { xs: 3, md: 6 },
        color: '#1a1a1a',
        background:
          'linear-gradient(180deg, #f7f7f9 0%, #f0efe9 60%, #e9e6dc 100%)',
        backgroundImage:
          'radial-gradient(rgba(0,0,0,0.025) 1px, transparent 1px), linear-gradient(180deg, #f7f7f9 0%, #f0efe9 60%, #e9e6dc 100%)',
        backgroundSize: '12px 12px, 100% 100%',
      }}
    >
      <CenteredTitle title="OFRECEMOS" className="custom-title" />

      <Grid
        container
        spacing={3}
        justifyContent="center"
        alignItems="stretch"
        sx={{ mt: 1 }}
      >
        {cardData.map((card) => (
          <Grid item key={card.id} xs={12} sm={6} md={4} lg={3}>
            <ModernCard
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              elevation={0}
            >
              {/* ===== IMAGEN ===== */}
              <MediaWrap>
                {!imgLoaded[card.id] && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,.12)',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                )}

                <Media
                  component="img"
                  alt={card.title}
                  image={card.image}
                  loaded={imgLoaded[card.id] ? 1 : 0}
                  onLoad={() =>
                    setImgLoaded((s) => ({ ...s, [card.id]: true }))
                  }
                  decoding="async"
                  loading="lazy"
                />

                <MediaOverlayTop />
                <MediaOverlayBottom />

                <Tooltip title="Ver grande" arrow>
                  <PreviewButton
                    aria-label="ver-imagen-grande"
                    onClick={() => openLightbox(card.image, card.title)}
                  >
                    <OpenInFullRoundedIcon fontSize="small" />
                  </PreviewButton>
                </Tooltip>
              </MediaWrap>

              {/* ===== CONTENIDO ===== */}
              <CardContent sx={{ flexGrow: 1, py: 1.5, px: 2 }}>
                <Typography
                  className="custom-second"
                  gutterBottom
                  variant="h6"
                  component="div"
                  sx={{
                    color: '#F5DEB3',
                    textShadow: '0 1px 0 rgba(0,0,0,.6)',
                    fontWeight: 800,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.25,
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,.78)',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {card.description}
                </Typography>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <SolidButton
                  component="a"
                  href={card.whas}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  disableElevation
                >
                  COMPRAR
                </SolidButton>
              </CardActions>
            </ModernCard>
          </Grid>
        ))}
      </Grid>

      {/* ===== LIGHTBOX ===== */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0b0b0b',
            border: '1px solid rgba(218,165,32,.25)',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            p: { xs: 2, md: 3 },
          }}
          onWheel={handleWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 2,
              color: '#F5DEB3',
              background: 'rgba(20,20,20,.6)',
              border: '1px solid rgba(218,165,32,.35)',
              '&:hover': { background: '#000' },
            }}
            aria-label="cerrar"
          >
            <CloseRoundedIcon />
          </IconButton>

          <Typography
            variant="subtitle1"
            sx={{
              color: '#f5f5f5',
              mb: 2,
              pr: 6,
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            {lightboxTitle}
          </Typography>

          <Box
            sx={{
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              border: '1px solid rgba(218,165,32,.2)',
              background:
                'radial-gradient(90% 80% at 50% 40%, rgba(255,255,255,.06) 0%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #111 0%, #070707 100%)',
              p: { xs: 1, md: 2 },
            }}
          >
            {lightboxSrc && (
              <ZoomableImg
                ref={imgRef}
                src={lightboxSrc}
                alt={lightboxTitle}
                style={{
                  transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                }}
                draggable={false}
              />
            )}
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'rgba(255,255,255,.65)',
              mt: 1,
            }}
          >
            Sugerencia: acercá/alejá con la rueda del mouse o pellizcando en el
            celular. Arrastrá para mover.
          </Typography>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Galeria;
